// Module-level registry of every prefix `getConstantsByFeatureName`
// has ever produced. Two features sharing a prefix means every
// action one dispatches is also picked up by the other's
// `takeEvery` handlers — see TECH_DEBT.md for the english-test-types
// vs. applicant-documents incident that bounced /english-test-types
// to /page-not-found via a saga that didn't belong to it.
//
// This is a soft check: it logs a console.error in development so
// the duplicate is loud, but doesn't throw — throwing would break
// the app entirely on a typo, and HMR can re-evaluate a module and
// register the same prefix twice during dev.
const seenPrefixes = new Map();

export default (featureName) => {
  if (typeof featureName !== 'string' || !featureName) {
    throw new Error(
      'getConstantsByFeatureName: featureName must be a non-empty string',
    );
  }
  // Allow re-registration in dev (HMR) — the registry only flags
  // distinct call-sites, which we approximate by stack frame.
  // Without source maps we can't be perfect; settle for best-effort.
  const callerFrame = new Error().stack?.split('\n')[2] ?? '';
  const prior = seenPrefixes.get(featureName);
  if (prior && prior !== callerFrame) {
    // eslint-disable-next-line no-console
    console.error(
      `[getConstantsByFeatureName] duplicate prefix "${featureName}" — `
        + 'two redux modules will share action types and every dispatch '
        + 'will fire both sagas. Pick a unique prefix per feature.\n'
        + `  first registration: ${prior}\n`
        + `  second registration: ${callerFrame}`,
    );
  } else {
    seenPrefixes.set(featureName, callerFrame);
  }

  return {
    LOAD_ITEMS_REQUEST: `@${featureName}/LOAD_ITEMS_REQUEST`,
    ITEMS_LOADED_SUCCESSFULLY: `@${featureName}/ITEMS_LOADED_SUCCESSFULLY`,
    LOAD_ITEMS_ERROR: `@${featureName}/LOAD_ITEMS_ERROR`,

    LOAD_ITEM_REQUEST: `@${featureName}/LOAD_ITEM_REQUEST`,
    ADD_LOADED_ITEM: `@${featureName}/ADD_LOADED_ITEM`,
    LOAD_ITEM_ERROR: `@${featureName}/LOAD_ITEM_ERROR`,

    CREATE_ITEM_REQUEST: `@${featureName}/CREATE_ITEM_REQUEST`,
    ADD_CREATED_ITEM: `@${featureName}/ADD_CREATED_ITEM`,
    CREATE_ITEM_ERROR: `@${featureName}/CREATE_ITEM_ERROR`,

    UPDATE_ITEM_REQUEST: `@${featureName}/UPDATE_ITEM_REQUEST`,
    ITEM_UPDATED_SUCCESSFULLY: `@${featureName}/ITEM_UPDATED_SUCCESSFULLY`,
    UPDATE_ITEM_ERROR: `@${featureName}/UPDATE_ITEM_ERROR`,

    DELETE_ITEM_REQUEST: `@${featureName}/DELETE_ITEM_REQUEST`,
    ITEM_DELETED_SUCCESSFULLY: `@${featureName}/ITEM_DELETED_SUCCESSFULLY`,
    DELETE_ITEM_ERROR: `@${featureName}/DELETE_ITEM_ERROR`,

    CHANGE_PAGE_NUMBER: `@${featureName}/CHANGE_PAGE_NUMBER`,
    CHANGE_PAGE_ITEMS_COUNT: `@${featureName}/CHANGE_PAGE_ITEMS_COUNT`,

    CHANGE_FILTER: `@${featureName}/CHANGE_FILTER`,
    REMOVE_FILTER: `@${featureName}/REMOVE_FILTER`,
    CLEAR_FILTER: `@${featureName}/CLEAR_FILTER`,
    SELECT_ALL_FILTERS: `@${featureName}/SELECT_ALL_FILTERS`,
    APPLY_FILTER: `@${featureName}/APPLY_FILTER`,
    CANCEL_FILTER: `@${featureName}/CANCEL_FILTER`,
    OPEN_FILTER: `@${featureName}/OPEN_FILTER`,
    CLOSE_FILTER: `@${featureName}/CLOSE_FILTER`,

    CHANGE_SEARCH: `@${featureName}/CHANGE_SEARCH`,
    CLEAR_SEARCH: `@${featureName}/CLEAR_SEARCH`,
    APPLY_SEARCH: `@${featureName}/APPLY_SEARCH`,

    SORT_ITEMS: `@${featureName}/SORT_ITEMS`,
    SORT_WITH_DIRECTION: `@${featureName}/SORT_WITH_DIRECTION`,

    CHANGE_STATE: `@${featureName}/CHANGE_STATE`,
    CLEAR_STATE: `@${featureName}/CLEAR_STATE`,

    UPDATE_STATE_BY_PARAMS: `@${featureName}/UPDATE_STATE_BY_PARAMS`,
  };
};
